
import { Box, Button, Checkbox, Dialog, DialogContent, MenuItem, TextField, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import ComboBox from "components/ComboBox"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { tyrePosList } from "branch/constant/tyrePosLlist"
import { addIdForDataGrid } from "utils"
import { tyreColNm, trnsTypNm } from "./constant"
import { alertError } from "components/Alert"

const REMOVE_OPTIONS = ['REMOVE', 'REMOVE_RTD', 'DISPOSE']

const TyreSwapDialog = ({ dialogOpen, setDialogOpen, onFinish }) => {
  const { ax, msData } = useContext(GlobalStateContext)

  const [tukId, setTukId] = useState(null)
  const [tlrId, setTlrId] = useState(null)
  const [trnsDte, setTrnsDte] = useState(dayjs().format("YYYY-MM-DD"))
  const [mileage, setMileage] = useState("")
  const [currentTyres, setCurrentTyres] = useState([])
  const [swapInTyres, setSwapInTyres] = useState([])
  const [stockTyres, setStockTyres] = useState([])
  const [stockFilter, setStockFilter] = useState("")
  const [manualRemovedIds, setManualRemovedIds] = useState(new Set())
  const [removeTrnsTypMap, setRemoveTrnsTypMap] = useState({})  // {TyrId: 'REMOVE'|'REMOVE_RTD'|'DISPOSE'}
  const [removeDscpMap, setRemoveDscpMap] = useState({})        // {TyrId: string}
  const [attempted, setAttempted] = useState(false)

  const loadCurrentTyres = useCallback((newTukId, newTlrId) => {
    if (!newTukId && !newTlrId) {
      setCurrentTyres([])
      return
    }
    const filter = { TyrStsId: 2 }
    if (newTukId) filter.TukId = newTukId
    if (newTlrId) filter.TlrId = newTlrId
    ax.post("/tyre/getTyre", filter).then(value => {
      if (value.data) setCurrentTyres(addIdForDataGrid(value.data, 'TyrId'))
    })
  }, [ax])

  const loadStockTyres = useCallback(() => {
    ax.post("/tyre/getTyre", { TyrStsId: 1 }).then(value => {
      if (value.data) setStockTyres(addIdForDataGrid(value.data, 'TyrId'))
    })
  }, [ax])

  useEffect(() => {
    if (dialogOpen) {
      loadStockTyres()
    } else {
      setTukId(null)
      setTlrId(null)
      setTrnsDte(dayjs().format("YYYY-MM-DD"))
      setMileage("")
      setCurrentTyres([])
      setSwapInTyres([])
      setStockFilter("")
      setManualRemovedIds(new Set())
      setRemoveTrnsTypMap({})
      setRemoveDscpMap({})
      setAttempted(false)
    }
  }, [dialogOpen, loadStockTyres])

  // Positions occupied by new tyres → auto-mark current tyres at those positions for removal
  const autoRemovedIds = useMemo(() => {
    const selectedPositions = new Set(swapInTyres.map(s => s.TyrPos).filter(Boolean))
    return new Set(currentTyres.filter(t => selectedPositions.has(t.TyrPos)).map(t => t.TyrId))
  }, [currentTyres, swapInTyres])

  const allRemovedIds = useMemo(
    () => new Set([...manualRemovedIds, ...autoRemovedIds]),
    [manualRemovedIds, autoRemovedIds]
  )

  const handleTukChange = useCallback((id) => {
    setTukId(id)
    setTlrId(null)
    setManualRemovedIds(new Set())
    setRemoveTrnsTypMap({})
    setRemoveDscpMap({})
    loadCurrentTyres(id, null)
  }, [loadCurrentTyres])

  const handleTlrChange = useCallback((id) => {
    setTlrId(id)
    setTukId(null)
    setManualRemovedIds(new Set())
    setRemoveTrnsTypMap({})
    setRemoveDscpMap({})
    loadCurrentTyres(null, id)
  }, [loadCurrentTyres])

  const handleToggleManualRemove = useCallback((tyrId) => {
    setManualRemovedIds(prev => {
      const next = new Set(prev)
      if (next.has(tyrId)) next.delete(tyrId)
      else next.add(tyrId)
      return next
    })
  }, [])

  const handleSelectStock = useCallback((row) => {
    if (swapInTyres.some(t => t.TyrId === row.TyrId)) return
    setSwapInTyres(prev => [...prev, { ...row, TyrPos: "" }])
  }, [swapInTyres])

  const handleRemoveSwapIn = useCallback((tyrId) => {
    setSwapInTyres(prev => prev.filter(t => t.TyrId !== tyrId))
  }, [])

  const handleSwapInPosChange = useCallback((tyrId, pos) => {
    setSwapInTyres(prev => prev.map(t => t.TyrId === tyrId ? { ...t, TyrPos: pos } : t))
  }, [])

  const handleRemoveTrnsTypChange = useCallback((tyrId, trnsTyp) => {
    setRemoveTrnsTypMap(prev => ({ ...prev, [tyrId]: trnsTyp }))
  }, [])

  const handleRemoveDscpChange = useCallback((tyrId, val) => {
    setRemoveDscpMap(prev => ({ ...prev, [tyrId]: val }))
  }, [])

  const handleDialogOk = useCallback(async () => {
    setAttempted(true)
    if (!tukId && !tlrId) {
      alertError("กรุณาเลือกรถ หรือ หางพ่วง")
      return
    }
    if (!mileage){
      alertError("กรุณาใส่เลขไมล์")
      return
    }
    const missingPos = swapInTyres.find(t => !t.TyrPos)
    if (missingPos) {
      alertError(`กรุณาเลือกตำแหน่งของยาง ${missingPos.TyrCd}`)
      return
    }
    const removeTyres = [...allRemovedIds].map(tyrId => ({
      TyrId: tyrId,
      TrnsTyp: removeTrnsTypMap[tyrId] ?? 'REMOVE_RTD',
      Dscp: removeDscpMap[tyrId] || null,
    }))
    const installTyres = swapInTyres.map(t => ({ TyrId: t.TyrId, TyrPos: t.TyrPos }))
    if (removeTyres.length === 0 && installTyres.length === 0) {
      setDialogOpen(false)
      return
    }
    const value = await ax.post("/tyre/swapTyre", {
      TukId: tukId || null,
      TlrId: tlrId || null,
      TrnsDte: trnsDte,
      Mileage: mileage || null,
      RemoveTyres: removeTyres,
      InstallTyres: installTyres,
    })
    if (value.data) {
      onFinish()
      setDialogOpen(false)
    }
  }, [ax, allRemovedIds, removeTrnsTypMap, removeDscpMap, swapInTyres, tukId, tlrId, trnsDte, mileage, onFinish, setDialogOpen])

  const filteredStock = useMemo(() => {
    const alreadyInSwap = new Set(swapInTyres.map(t => t.TyrId))
    return stockTyres.filter(t =>
      (!stockFilter || t.TyrCd?.toLowerCase().includes(stockFilter.toLowerCase())) &&
      !alreadyInSwap.has(t.TyrId)
    )
  }, [stockTyres, stockFilter, swapInTyres])

  const currentColumns = useMemo(() => [
    {
      field: 'Remove',
      headerName: 'ถอด',
      width: 55,
      align: 'center',
      renderCell: ({ row }) => {
        const isAuto = autoRemovedIds.has(row.TyrId)
        return (
          <Checkbox
            size="small"
            checked={allRemovedIds.has(row.TyrId)}
            disabled={isAuto}
            onChange={() => handleToggleManualRemove(row.TyrId)}
            color={isAuto ? "warning" : "error"}
          />
        )
      }
    },
    {
      field: 'TrnsTyp',
      headerName: 'ประเภท',
      width: 175,
      renderCell: ({ row }) => {
        const isRemoved = allRemovedIds.has(row.TyrId)
        return (
          <TextField
            select size="small" fullWidth
            disabled={!isRemoved}
            value={isRemoved ? (removeTrnsTypMap[row.TyrId] ?? 'REMOVE_RTD') : ""}
            onChange={(e) => handleRemoveTrnsTypChange(row.TyrId, e.target.value)}
            onClick={(e) => e.stopPropagation()}
          >
            {REMOVE_OPTIONS.map(typ => (
              <MenuItem key={typ} value={typ}>{trnsTypNm[typ]}</MenuItem>
            ))}
          </TextField>
        )
      }
    },
    { field: 'TyrPos', headerName: tyreColNm.TyrPos, width: 65, align: 'center' },
    { field: 'TyrCd', headerName: tyreColNm.TyrCd, width: 115 },
    { field: 'TyrBrand', headerName: tyreColNm.TyrBrand, width: 100 },
    { field: 'TyrSzNm', headerName: tyreColNm.TyrSzId, width: 100 },
    {
      field: 'Dscp',
      headerName: tyreColNm.Dscp,
      flex: 1,
      minWidth: 140,
      renderCell: ({ row }) => {
        const isRemoved = allRemovedIds.has(row.TyrId)
        return (
          <TextField
            size="small" fullWidth
            placeholder="เหตุผลที่ถอด"
            disabled={!isRemoved}
            value={removeDscpMap[row.TyrId] ?? ""}
            onChange={(e) => handleRemoveDscpChange(row.TyrId, e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        )
      }
    },
  ], [autoRemovedIds, allRemovedIds, removeTrnsTypMap, removeDscpMap, handleToggleManualRemove, handleRemoveTrnsTypChange, handleRemoveDscpChange])

  const swapInColumns = useMemo(() => [
    {
      field: 'TyrPos',
      headerName: tyreColNm.TyrPos,
      width: 115,
      renderCell: ({ row }) => (
        <TextField
          select size="small" fullWidth
          value={row.TyrPos || ""}
          error={attempted && !row.TyrPos}
          onChange={(e) => handleSwapInPosChange(row.TyrId, e.target.value)}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem value=""><em>-</em></MenuItem>
          {tyrePosList.map(pos => (
            <MenuItem key={pos} value={pos}>{pos}</MenuItem>
          ))}
        </TextField>
      )
    },
    { field: 'TyrCd', headerName: tyreColNm.TyrCd, width: 215 },
    { field: 'TyrBrand', headerName: tyreColNm.TyrBrand, width: 100 },
    { field: 'TyrSzNm', headerName: tyreColNm.TyrSzId, flex: 1, minWidth: 90 },
    {
      field: 'Del',
      headerName: '',
      width: 55,
      align: 'center',
      renderCell: ({ row }) => (
        <Button size="small" color="error"
          onClick={(e) => { e.stopPropagation(); handleRemoveSwapIn(row.TyrId) }}>
          ลบ
        </Button>
      )
    },
  ], [attempted, handleSwapInPosChange, handleRemoveSwapIn])

  const stockColumns = useMemo(() => [
    {
      field: 'Select',
      headerName: '',
      width: 70,
      align: 'center',
      renderCell: ({ row }) => (
        <Button size="small" variant="contained"
          onClick={(e) => { e.stopPropagation(); handleSelectStock(row) }}>
          เลือก
        </Button>
      )
    },
    { field: 'TyrCd', headerName: tyreColNm.TyrCd, width: 190 },
    { field: 'TyrBrand', headerName: tyreColNm.TyrBrand, width: 110 },
    { field: 'TyrSzNm', headerName: tyreColNm.TyrSzId, width: 110 },
    { field: 'TyrModel', headerName: tyreColNm.TyrModel, flex: 1, minWidth: 90 },
    {
      field: 'RetreadCnt', headerName: 'หล่อดอก(ครั้ง)', width: 90, align: 'center',
      valueFormatter: ({ value }) => value || '-',
    },
  ], [handleSelectStock])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => { if (reason === "backdropClick") return; setDialogOpen(false) }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='xl'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)} title="ถอด-ใส่ยาง" />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1, height: 700 }}>
          {/* Top bar: date / truck / trailer / mileage */}
          <BoxFR sx={{ gap: 1 }}>
            <DesktopDatePicker
              label="วันที่ทำรายการ"
              inputFormat="DD/MM/YYYY"
              value={trnsDte}
              onChange={(v) => setTrnsDte(v ? dayjs(v).format("YYYY-MM-DD") : null)}
              renderInput={(params) => <TextField size="small" sx={{ width: 160 }} {...params} error={!trnsDte}/>}
            />
            <ComboBox
              options={msData.truckCombo || []}
              label="รถ"
              selectedId={tukId}
              setSelectedId={handleTukChange}
              sx={{ width: 180 }}
              error={attempted && !tukId && !tlrId}

            />
            <ComboBox
              options={msData.trailerCombo || []}
              label="หางพ่วง"
              selectedId={tlrId}
              setSelectedId={handleTlrChange}
              sx={{ width: 180 }}
              error={attempted && !tukId && !tlrId}
            />
            <TextField
              size="small" label="เลขไมล์ (กม.)" type="number"
              value={mileage}
              required={true}
              error={!mileage}
              onChange={(e) => setMileage(e.target.value)}
              sx={{ width: 160 }}
            />
          </BoxFR>

          {/* Main body: left (current + new) | right (stock) */}
          <BoxFR sx={{ gap: 1, flex: 1, overflow: 'hidden', alignItems: 'stretch' }}>

            {/* Left side */}
            <BoxFC sx={{ flex: 1.2, gap: 1, overflow: 'hidden' }}>

              {/* Current installed tyres */}
              <BoxFC sx={{ flex: 1, overflow: 'hidden', mt: 2 }}>
                <Typography variant="h6" >
                  ยางที่ติดตั้งอยู่ปัจจุบัน ({currentTyres.length})
                </Typography>
                <Box flex={1} overflow='hidden' sx={{mt: -2}}>
                  <DataGridCellExpand
                    hideFooter
                    rows={currentTyres}
                    columns={currentColumns}
                    getRowClassName={({ row }) => allRemovedIds.has(row.TyrId) ? 'row-removed' : ''}
                    sx={{
                      '& .row-removed .MuiDataGrid-cell': {
                        // textDecoration: 'line-through',
                        color: 'text.disabled',
                      }
                    }}
                  />
                </Box>
              </BoxFC>

              {/* Swap-in (new tyres to install) */}
              <BoxFC sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography variant="h6" >
                  ยางที่จะใส่ใหม่ ({swapInTyres.length})
                </Typography>
                <Box flex={1} overflow='hidden' sx={{mt: -2}}>
                  <DataGridCellExpand
                    hideFooter
                    rows={swapInTyres.map(t => ({ ...t, id: t.TyrId }))}
                    columns={swapInColumns}
                  />
                </Box>
              </BoxFC>
            </BoxFC>

            {/* Right side: stock */}
            <BoxFC sx={{ flex: 1, gap: 1, overflow: 'hidden' }}>
              <BoxFR sx={{ gap: 1 }}>
                <Typography variant="h6">ยางในสต็อก</Typography>
                <TextField
                  size="small" label="รหัสยาง" sx={{ width: 160 }}
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                />
              </BoxFR>
              <Box flex={1} overflow='hidden'>
                <DataGridCellExpand
                  hideFooter
                  rows={filteredStock}
                  columns={stockColumns}
                />
              </Box>
            </BoxFC>

          </BoxFR>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="บันทึก"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk}
      />
    </Dialog>
  )
}

export default React.memo(TyreSwapDialog)
